import { Fragment, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import { ScheduleContext } from "../context/schedule.context";
import { COLUMN_DAY_ARRAY } from "../utility/keys";
import { getCellLockEligibility } from "../utility/lockEligibility";
import { buildLockMenuItem } from "../utility/lockMenuItem";
import UnassignedProjects from "../components/UnassignedProjects";
import {
  recordRowHeight,
  computeSyncedRowMinHeight,
} from "../utility/diffPairRowHeights";
import JobTile from "./schedule/JobTile";
import SharedLocationsGroup from "./schedule/SharedLocationsGroup";
import { cellEntryGroup } from "../utility/cellEntryGroup";
import ScheduleCell from "./schedule/ScheduleCell";
// (#79) Removed unused imports:
//   - ScheduleHeader: #105 multi-grid replaced <ScheduleHeader> with the
//     GlobalToolbar / PerGridToolbar architecture. The string
//     "ScheduleHeader" still appears in a console.log debug tag, but
//     that's not a component reference.
//   - Lock + LockOpen: their only JSX usage in this file is inside a
//     JSX comment block ({/* ... */}) at the column-header lock-toggle
//     scaffold. Re-add when that scaffolding is uncommented and the
//     lock feature is built.
import { TooltipPortal } from "../components/TooltipPortal"; // adjust path if needed
import { Splitter, useSplitterWidth } from "../components/Splitter";
import { OptimizerGrid } from "./schedule/OptimizerGrid";
import GlobalToolbar from "./schedule/GlobalToolbar";
import ContextMenu from "../components/ContextMenu";
import { ScheduleContextProvider } from "../context/schedule.context";
import {
  getMondayOf,
  getMondayOfThisWeek,
} from "../utility/weekDates";
import { resolveRestoredWeek } from "../utility/optimizerWeekRestore";
import { FavInboxContext } from "../context/favInbox.context";
import { gridWeekMondayOf, ghostCellKey, jobKey } from "../utility/favInbox";
import FavGhostTile from "../components/FavGhostTile";
import CrewScoresFetcher from "../components/CrewScoresFetcher";
import { formatCrewLtrLine, crewPayrollList } from "../utility/crewScoreLabel";

// localStorage key for the grids workspace (#105 phase 4c).
// Stored as JSON: [{ id, weekStartDate (ISO string), width? }, ...]
// width is the persisted pixel width (#105 phase 5); null/undefined means
// the grid uses flex-1 (equal share of remaining space).
const GRID_WORKSPACE_KEY = "scheduler.optimizer.gridWorkspace";

// Minimum pixel width when dragging a grid splitter. Below this the date
// columns get crushed; tunable.
const MIN_GRID_WIDTH = 400;
const MAX_GRID_WIDTH = 2400;

// NOTE: got help with filtering dates, using start & end values, for overlapping calculations from webpage:
//        https://stackoverflow.com/questions/17304426/javascript-date-range-between-date-range
//              An overlap test is slightly more complex. The following will return true if the two date ranges overlap, regardless of order.

export const OptimizerScreen = ({ children }) => {
  const {
    locationFilter,
    companyCode,
    countsToggle,
    // (#79) Removed from destructure — unused at OptimizerScreen scope:
    //   - weekDatesArray: the per-grid week is owned by each <OptimizerGrid>
    //     since #105 phase 4b. The two helpers below (isLocked,
    //     renderDateHeaderRow) take weekDatesArray as a parameter that
    //     shadows any outer name, so the AppContext value isn't read here.
    //   - lockAccess_assigned: only referenced in commented onLockClick.
    setOpenJobsDetailModal,
    setOpenCrewDetailModal,
    setOpenCrewMetaModal,
    setOpenExtraCellAddModal,
    setOpenExtraCellDetailModal,
    dataFormatting_payrollFields,
    parameterWeek,
    isTimeTraveling,
    isDiffPairView,
    setIsDiffPairView,
    isDiffHighlightsMuted,
  } = useContext(AppContext);
  const {
    awsgwCandidatesResult,
    awsgwCandidatesResultLocation,
    // Time Machine #114 Phase 6 — projectDiffs/attributeDiffs computed by
    // computeScheduleDiff in ModelCxContext. Null outside Diff Pair view.
    timeTravelDiff,
    // SUP-9005 — toggle a day's lock from the job-tile right-click menu
    // (same mutation the cell menu uses).
    tsToggleCellLock,
  } = useContext(ModelCxContext);
  // Use TanStack candidates - return empty array if data is stale (wrong location)
  const filteredCandidatesList = awsgwCandidatesResultLocation === locationFilter
    ? awsgwCandidatesResult
    : [];

  // #695 — each crew's LTR survey score (Convex crewScores), keyed by 7-char payroll.
  const [crewLtrByPayroll, setCrewLtrByPayroll] = useState(() => new Map());
  // Keyed by the joined list so the subscription's args only change when the crews do.
  const crewPayrollKey = crewPayrollList(filteredCandidatesList, dataFormatting_payrollFields).join("|");
  const crewPayrolls = useMemo(() => (crewPayrollKey ? crewPayrollKey.split("|") : []), [crewPayrollKey]);

  // Phase 6: build a Map<mongoId, projectDiff> once per timeTravelDiff change
  // so the per-tile lookup in renderXxxScheduledCells is O(1). The schedule
  // array's project objects expose the canonical mongo _id as `mongoId`
  // (Phase 5's buildScheduleArrayForWeek fix; live mode also populates
  // mongoId via the TanStack `select`). Empty map outside Diff Pair view.
  const projectDiffMap = useMemo(() => {
    const m = new Map();
    if (!timeTravelDiff || !Array.isArray(timeTravelDiff.projectDiffs)) return m;
    for (const d of timeTravelDiff.projectDiffs) {
      if (d?.id != null) m.set(String(d.id), d);
    }
    return m;
  }, [timeTravelDiff]);
  // Phase 6: lookup for attribute diffs, indexed by attribute mongo _id.
  // The schedule array's attribute pseudo-jobs (newJob.attribute === true)
  // copy the source attribute's _id into the pseudo-job, so this map can
  // drive per-tile bubbles on the attribute branch of JobTile.
  const attributeDiffMapById = useMemo(() => {
    const m = new Map();
    if (!timeTravelDiff || !Array.isArray(timeTravelDiff.attributeDiffs)) return m;
    for (const d of timeTravelDiff.attributeDiffs) {
      if (d?.id != null) m.set(String(d.id), d);
    }
    return m;
  }, [timeTravelDiff]);

  // Phase 6: helper picks the correct diff map based on the schedule array
  // entry kind. Attribute pseudo-jobs (project.attribute === true) match
  // attributeDiffMapById by _id; regular jobs match projectDiffMap by mongoId.
  const lookupDiffForProject = (project) => {
    if (!project) return null;
    if (project.attribute) {
      const id = project._id?.toString?.() ?? project._id;
      return id ? attributeDiffMapById.get(String(id)) || null : null;
    }
    const id = project.mongoId || project._id?.toString?.() || project._id;
    return id ? projectDiffMap.get(String(id)) || null : null;
  };
  const {
    scheduleArray,
    // (#79) Removed from destructure — unused at OptimizerScreen scope:
    //   - countsArray: per-grid since #105 phase 4b. renderDateHeaderRow
    //     takes it as a parameter that shadows.
    //   - showOffInstallers: filter logic moved into <OptimizerGrid> in
    //     #105 phase 4b (see file-level comment near line 78). The
    //     AppContext-level value isn't read here anymore.
    schedulePreparing,
    setDragObject,
    setDragObjectInvoiceNo,
    setDropObject,
    selectedUnscheduledJob,
    // Job Parking
    isJobParked,
    parkJob,
  } = useContext(ScheduleContext);
  // FAV 2.0 ghosts (#642) look their job up in the FULL list — renderAssignedScheduledCells'
  // own `scheduleArray` parameter is the per-grid week and shadows this one.
  const allProjectsForGhosts = scheduleArray;

  // visibleCandidatesList moved into OptimizerGrid (#105 phase 4b) — each
  // grid now computes its own based on its local week, so the filter is
  // per-grid in the multi-grid future. filteredCandidatesList stays here
  // (location-based, shared across all grids) and is passed down as a prop.

  // - - - - - - - - - -

  const [tooltip, setTooltip] = useState({
    show: false,
    text: "",
    position: { left: 0, top: 0 },
  });

  // - - - - - - - - - -
  // RTS sidebar resizable width (#104). Persists per-browser via localStorage.

  const {
    width: rtsSidebarWidth,
    isDragging: isResizingSidebar,
    startDrag: startSidebarResize,
  } = useSplitterWidth("scheduler.optimizer.rtsSidebarWidth");

  // - - - - - - - - - -
  // Multi-grid workspace (#105 phase 4c). Each entry is { id, weekStartDate }.
  // Persisted per-browser. New grids default to the rightmost grid's week
  // and are inserted to the right of the right-clicked grid (if context-menu)
  // or to the very end (if no specific anchor).

  const [grids, setGrids] = useState(() => {
    try {
      const stored = localStorage.getItem(GRID_WORKSPACE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // #422 — a stored week that has since aged out of the attributes
          // window is advanced to TODAY. Per-grid, so a still-valid sibling
          // keeps its week (two grids on different weeks is a real feature).
          // Without this, a returning user lands on a week whose availability
          // notes save but can never be read back — see #415.
          return parsed
            .filter((g) => g && g.id && g.weekStartDate)
            .map((g) => ({
              id: g.id,
              weekStartDate: resolveRestoredWeek(g.weekStartDate),
              width: typeof g.width === "number" ? g.width : null,
            }));
        }
      }
    } catch (e) {
      // fall through
    }
    // One-time migration from phase 4b's single-key scheme.
    try {
      const legacy = localStorage.getItem(
        "scheduler.optimizer.gridWeekStartDate"
      );
      if (legacy) {
        const parsed = new Date(legacy);
        if (!isNaN(parsed.getTime())) {
          localStorage.removeItem("scheduler.optimizer.gridWeekStartDate");
          // #422 — this path restores a stored date too, so it needs the same
          // staleness guard. A legacy key is by definition old.
          return [
            {
              id: "grid-1",
              weekStartDate: resolveRestoredWeek(parsed),
              width: null,
            },
          ];
        }
      }
    } catch (e) {
      // fall through
    }
    // Default: single grid at this week.
    return [
      { id: "grid-1", weekStartDate: getMondayOfThisWeek(), width: null },
    ];
  });

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(
        GRID_WORKSPACE_KEY,
        JSON.stringify(
          grids.map((g) => ({
            id: g.id,
            weekStartDate: g.weekStartDate.toISOString(),
            width: g.width,
          }))
        )
      );
    } catch (e) {
      // non-fatal
    }
  }, [grids]);

  // External URL with ?week=... parameter — apply to grid #1 (the leftmost).
  // Without this, AppContext.weekDatesArray would update (and the legacy
  // Scheduler tab would honor it), but each OptimizerGrid manages its own
  // week locally, so the URL date wouldn't reach any grid. Only the first
  // grid is updated; other grids keep whatever week the user navigated them
  // to. ModelCxContext's separate parameterWeek useEffect clears
  // parameterWeek back to "" — the !parameterWeek guard here prevents
  // re-firing on that clear.
  useEffect(() => {
    if (!parameterWeek) return;
    const targetMonday = getMondayOf(new Date(parameterWeek));
    if (isNaN(targetMonday.getTime())) return;
    setGrids((prev) =>
      prev.length > 0
        ? prev.map((g, i) =>
            i === 0 ? { ...g, weekStartDate: targetMonday } : g
          )
        : prev
    );
  }, [parameterWeek]);

  // FAV Inbox (#630): clicking a booking moves the FIRST grid to its week (the same
  // grid a ?week= link moves, above); the provider then scrolls to the tile + flashes it.
  const { jumpRequest: favJumpRequest, ghostsByCell: favGhostsByCell } = useContext(FavInboxContext);
  useEffect(() => {
    if (!favJumpRequest) return;
    const targetMonday = gridWeekMondayOf(favJumpRequest.bookedDate);
    if (!targetMonday) return;
    setGrids((prev) =>
      prev.length > 0
        ? prev.map((g, i) =>
            i === 0 && g.weekStartDate?.getTime?.() !== targetMonday.getTime() ? { ...g, weekStartDate: targetMonday } : g
          )
        : prev
    );
  }, [favJumpRequest]);

  // Refs to each grid wrapper for measuring current pixel width when
  // starting a splitter drag (#105 phase 5). Cleaned up on grid removal
  // automatically since React passes null to the ref callback when the
  // element unmounts.
  const gridWrapperRefs = useRef({});

  // Which grid's right-edge splitter is currently being dragged. Drives
  // the active color on the corresponding <Splitter>.
  const [draggingSplitterGridId, setDraggingSplitterGridId] = useState(null);

  const startSplitterDrag = (leftGridId) => (e) => {
    e.preventDefault();
    const leftGridEl = gridWrapperRefs.current[leftGridId];
    if (!leftGridEl) return;
    const startWidth = leftGridEl.offsetWidth;
    const startX = e.clientX;

    setDraggingSplitterGridId(leftGridId);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.min(
        MAX_GRID_WIDTH,
        Math.max(MIN_GRID_WIDTH, startWidth + delta)
      );
      setGrids((prev) =>
        prev.map((g) =>
          g.id === leftGridId ? { ...g, width: newWidth } : g
        )
      );
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setDraggingSplitterGridId(null);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const setGridWeek = (gridId, newWeekStartDate) => {
    setGrids((prev) =>
      prev.map((g) =>
        g.id === gridId ? { ...g, weekStartDate: newWeekStartDate } : g
      )
    );
  };

  const addGridAfter = (anchorGridId) => {
    setGrids((prev) => {
      const anchorIdx = prev.findIndex((g) => g.id === anchorGridId);
      const insertAt = anchorIdx >= 0 ? anchorIdx + 1 : prev.length;
      const rightmost = prev[prev.length - 1];
      const newGrid = {
        id: `grid-${Date.now()}`,
        weekStartDate: rightmost.weekStartDate,
      };
      const next = [...prev];
      next.splice(insertAt, 0, newGrid);
      return next;
    });
  };

  const closeGrid = (gridId) => {
    setGrids((prev) =>
      prev.length > 1 ? prev.filter((g) => g.id !== gridId) : prev
    );
  };

  // Right-click context menu state.
  const [contextMenu, setContextMenu] = useState(null);
  // null = hidden; { x, y, gridId } = visible at (x,y) anchored to gridId.

  // Document-level contextmenu handler so the menu works regardless of
  // React event-system quirks or stacking contexts. Bails out if a child
  // (e.g., JobTile's park menu) already handled the event by calling
  // preventDefault — that way right-clicking a job shows ONLY the park
  // menu, not both. The closest [data-grid-id] tells us which grid was
  // clicked so the menu's "Close this grid" knows which one to remove.
  useEffect(() => {
    const handler = (e) => {
      if (e.defaultPrevented) return; // already handled (e.g., JobTile)
      const gridElement = e.target.closest("[data-grid-id]");
      if (!gridElement) return; // not on a grid (e.g., RTS sidebar)
      e.preventDefault();
      const gridId = gridElement.getAttribute("data-grid-id");
      setContextMenu({ x: e.clientX, y: e.clientY, gridId });
    };
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  // - - - - - - - - - -

  const handleMouseEnter = (e, text) => {
    if (!text) return;
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      show: true,
      text,
      position: {
        left: rect.left + rect.width / 2,
        top: rect.bottom + 8, // 8px below the button
      },
    });
  };

  // - - - - - - - - - -

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", position: { left: 0, top: 0 } });
  };

  // - - - - - - - - - -

  /**
   * Handles the click event for project details.
   * @param {Object} project - The project object.
   * @param {Event} event - The click event.
   */
  const onProjectDetailClick = (project, event) => {
    if (project.location !== locationFilter) {
      // do not allow for shared installer jobs
      console.log(
        "OptimizerScreen onProjectDetailClick IGNORING, shared installer, location: ",
        project.location
      );
      return;
    }

    event?.stopPropagation();
    // document.getElementById(SCREENS_PROJECTDETAIL_ID).style.display = "block";
    // if (id !== "") {
    //   const projectDetail = scheduleArray.find(
    //     (document) => document.id === id
    //   );
    console.log("OptimizerScreen onProjectDetailClick, project: ", project);
    if (project.extraCell) {
      // extra cell
      setOpenExtraCellDetailModal({
        show: true,
        jobsDetail: [project],
      });
    } else {
      // job
      setOpenJobsDetailModal({ show: true, jobsDetail: [project] });
    }
  };

  // - - - - - - - - - -

  /**
   * Handles the click event for adding an unassigned project.
   */
  const onUnassignedAddClick = () => {
    const projectDetail = scheduleArray.find(
      (document) => document.start === "1/10/2025"
    );
    console.log(
      "OptimizerScreen onUnassignedDetailClick, projectDetail: ",
      projectDetail
    );
    // setOpenUnassignedDetailModal({ show: true });
    setOpenExtraCellAddModal({ show: true, jobsDetail: projectDetail });
  };

  // - - - - - - - - - -

  /**
   * Handles the click event for crew details.
   *
   * @param {string} id - The crew ID.
   * @param {Array<string>} [weekDatesArray] - The per-grid week-dates array
   *   from the <OptimizerGrid> the user clicked in. Passed through to the
   *   modal so its Availability tab edits the same week the grid is showing.
   *   Pre-#132 (the multi-grid week-decoupling bug fix) this was implicitly
   *   sourced from AppContext, which since #105 multi-grid is decoupled from
   *   any specific grid. Optional for the legacy Scheduler-tab caller; the
   *   modal falls back to AppContext when absent.
   */
  const onCrewDetailClick = (id, weekDatesArray) => {
    const newCrewDetail = filteredCandidatesList.find(
      (document) => document.__ID_Candidate === id
    );
    console.log(
      "OptimizerScreen onCrewDetailClick, id: ",
      id,
      "; payrollNo(atring): ",
      dataFormatting_payrollFields(newCrewDetail.payrollNo),
      "; weekDatesArray: ",
      weekDatesArray,
      "; crewDetail: ",
      newCrewDetail
    );
    setOpenCrewDetailModal({
      show: true,
      crewDetail: newCrewDetail,
      weekDatesArray,
    });
  };

  // - - - - - - - - - -

  /**
   * Handles the click event for crew meta data.
   * @param {string} id - The crew ID.
   */
  const onCrewMetaClick = (id) => {
    const newCrewDetail = filteredCandidatesList.find(
      (document) => document.__ID_Candidate === id
    );
    console.log("ScheduleHeader onCrewMetaClick...");
    setOpenCrewMetaModal({ show: true, currentCrew: newCrewDetail });
  };

  // - - - - - - - - - -

  /**
   * Allows the drop event to occur.
   * @param {Event} event - The drop event.
   */
  const allowDropHere = (event) => {
    event.preventDefault();
  };

  // - - - - - - - - - -

  const onCellDragOver = (e) => {
    allowDropHere(e);
    const el = e.currentTarget;
    el.style.border = "2px dashed #60a5fa";
    el.style.backgroundColor = "rgba(96, 165, 250, 0.1)";
    el.style.borderRadius = "4px";
  };

  const onCellDragLeave = (e) => {
    const el = e.currentTarget;
    el.style.border = "";
    el.style.backgroundColor = "";
    el.style.borderRadius = "";
  };

  const onCellDrop = (e, id) => {
    const el = e.currentTarget;
    el.style.border = "";
    el.style.backgroundColor = "";
    el.style.borderRadius = "";
    hasBeenDropped(e, id);
  };

  // - - - - - - - - - -

  /**
   * Handles the drag start event.
   * @param {string} id - The ID of the dragged element.
   * @param {number} invoiceNo - The invoice number for tanstackOnly fallback lookup.
   */
  const onDragStarted = (id, invoiceNo) => {
    // event.dataTransfer.setData("div", event.target.id);
    setDragObject(id);
    setDragObjectInvoiceNo(invoiceNo);
    console.log("OptimizerScreen dragStarted id:", id, ", invoiceNo:", invoiceNo);
  };

  // - - - - - - - - - -

  /**
   * Handles parking a job from the grid.
   * @param {Object} project - The project object to park.
   */
  const onParkJob = (project) => {
    parkJob(project.id, project.invoiceNo, project.installer, project.start);
    console.log("OptimizerScreen onParkJob:", project.invoiceNo);
  };

  // - - - - - - - - - -

  /**
   * Handles the drop event.
   * @param {Event} event - The drop event.
   * @param {string} id - The ID of the drop target.
   */
  const hasBeenDropped = (event, id) => {
    event.preventDefault();
    const droppedObject = { id: id };
    setDropObject(droppedObject);
    console.log(
      "OptimizerScreen hasBeenDropped id: ",
      id,
      ", NO content passed"
    );
  };

  // - - - - - - - - - -

  /**
   * Handles the click event for locking a cell.
   * @param {Event} event - The click event.
   * @param {string} installer - The installer ID.
   * @param {string} date - The date.
   */
  // const onLockClick = (event, installer, date) => {
  //   event.stopPropagation();
  //   if (installer === "column") {
  //     processBatchAttributes("column", "Lock", date);
  //     console.log("OptimizerScreen onLockClick, column lock date: ", date);

  //     //
  //   } else {
  //     // confirm lockAccess
  //     if (
  //       (installer === "Unassig" && lockAccess_unassigned) ||
  //       (installer !== "Unassig" && lockAccess_assigned)
  //     ) {
  //       updateAttributeList(
  //         date,
  //         dataFormatting_payrollFields(installer),
  //         "Lock",
  //         null
  //       );
  //       console.log(
  //         "OptimizerScreen onLockClick, date: ",
  //         date,
  //         ", installer: ",
  //         installer
  //       );
  //     } else {
  //       console.log(
  //         "OptimizerScreen onLockClick, access denied, date: ",
  //         date,
  //         ", installer: ",
  //         installer
  //       );
  //     }
  //   }
  // };

  // - - - - - - - - - -

  /**
   * Checks if a cell is locked.
   * @param {string} payrollNo - The payroll number.
   * @param {number} columnIndex - The column index.
   * @returns {boolean} - True if the cell is locked, false otherwise.
   */
  // weekDatesArray + scheduleArray taken as parameters (not closed over)
  // so each grid can supply its own week + per-week scheduleArray —
  // see #105 phase 2 (weekDatesArray) and phase 4b (scheduleArray).
  const isLocked = (payrollNo, columnIndex, weekDatesArray, scheduleArray) => {
    // ignoring COLUMN for now...
    if (payrollNo !== "column") {
      var payrollStr = "";
      var nonComplianceFlag = 0;
      if (payrollNo === "") {
        // unassigned cell
        payrollStr = "";
      } else {
        // assigned cell
        payrollStr = dataFormatting_payrollFields(payrollNo);

        // (#79) installerDocument lookup removed — only consumed by the now-
        // commented Scheduling_Hold_Flag check below. The compliance flow was
        // replaced by Scheduling_Hold_Effective_Date elsewhere; if anyone
        // re-enables the inline check, re-add the filter call.
        // 2025-05-19 - no longer disabling drag-n-drop for non-compliance; instead, ignore drop and present Alert
        // 2025-01-28 - DISABLED: Replaced by Scheduling_Hold_Effective_Date; keeping for potential re-enable
        // nonComplianceFlag = installerDocument[0]?.Scheduling_Hold_Flag;
        nonComplianceFlag = false;
      }
      // filter for matching date & installer & attribute (not job)
      const currentValueArray = scheduleArray?.filter(
        (document) =>
          payrollStr === document.installer &&
          weekDatesArray[columnIndex] === document.start &&
          document.attribute
      );
      // if found it should have only one match
      const filteredArraySize = currentValueArray?.length;
      if (payrollStr !== "") {
        // console.log(
        //   payrollStr,
        //   " isLocked, column: ",
        //   columnIndex,
        //   ", weekDatesArray[columnIndex]: ",
        //   weekDatesArray[columnIndex],
        //   ", filteredArraySize: ",
        //   filteredArraySize,
        //   ", currentValueArray: ",
        //   currentValueArray,
        //   ", installerDocumentSize: ",
        //   installerDocument?.length,
        //   ", installerDocument: ",
        //   installerDocument,
        //   ", nonComplianceFlag: ",
        //   nonComplianceFlag
        // );
      }
      if (nonComplianceFlag) {
        // non-compliant so return true for isLocked here
        return true;
      } else {
        return filteredArraySize > 0 ? currentValueArray[0].locked : false;
      }
    } else {
      return false;
    }
  };

  // - - - - - - - - - -

  /**
   * Renders the date header row.
   * @returns {JSX.Element} - The rendered date header row.
   */
  // weekDatesArray + countsArray taken as parameters — phase 2 (weekDatesArray)
  // + phase 4b (countsArray) so each grid can pass its per-week values.
  const renderDateHeaderRow = (
    weekDatesArray,
    countsArray,
    noFirstSticky = false,
    cellWidthRem = null
  ) => {
    return COLUMN_DAY_ARRAY.map((value, index) => {
      const softJobs = countsArray[index] ? countsArray[index].softJobs : 0;
      const hardJobs = countsArray[index] ? countsArray[index].hardJobs : 0;
      const softSlots = countsArray[index] ? countsArray[index].softSlots : 0;
      const hardSlots = countsArray[index] ? countsArray[index].hardSlots : 0;
      const softFilledSlots = countsArray[index]
        ? countsArray[index].softFilledSlots.size
        : 0;
      const hardFilledSlots = countsArray[index]
        ? countsArray[index].hardFilledSlots.size
        : 0;
      return (
        <ScheduleCell
          key={index}
          className="bg-header nth-[n+7]:bg-header-lighter"
          noMinWidth={grids.length === 1}
          noFirstSticky={noFirstSticky}
          widthRem={cellWidthRem}
        >
          <div
            className="h-full border-b-2
            border-yellow text-sm flex flex-col justify-center items-center px-2"
          >
            {/* <div className="flex justify-between w-full"> */}
            <div className="flex justify-center w-full">
              <span className="flex-auto max-w-4.5"></span>
              <span className="font-medium text-nowrap">
                {value + " " + weekDatesArray[index]}
              </span>
              {/* {lockAccess_assigned ? (
                <button
                  className="fill-primary w-4.5"
                  // onClick={(e) =>
                  //   onLockClick(e, "column", weekDatesArray[index])
                  // }
                >
                  {false ? <Lock></Lock> : <LockOpen></LockOpen>}
                </button>
              ) : (
                <></>
              )} */}
            </div>
            <div className="flex justify-between w-full">
              <span>
                S:&nbsp;
                <span className="font-bold">
                  {countsToggle ? softFilledSlots : softJobs}
                </span>
                &nbsp;/&nbsp;
                <span className="font-bold">{softSlots}</span>
              </span>
              <span>
                H:&nbsp;
                <span className="font-bold">
                  {countsToggle ? hardFilledSlots : hardJobs}
                </span>
                &nbsp;/&nbsp;
                <span className="font-bold">{hardSlots}</span>
              </span>
            </div>
          </div>
        </ScheduleCell>
      );
    });
  };

  // - - - - - - - - - -

  /**
   * Renders the unassigned scheduled cells for a given column.
   * @param {number} columnIndex - The column index.
   * @returns {JSX.Element[]} - The rendered unassigned scheduled cells.
   */
  // weekDatesArray + scheduleArray taken as parameters — phase 2 + 4b.
  const renderUnassignedScheduledCells = (
    columnIndex,
    weekDatesArray,
    scheduleArray,
    showDiffs = false
  ) => {
    // added filter for empty encodedTitles to prevent locked cells without titles from displaying an empty string

    return scheduleArray
      .filter(
        (document) =>
          ((new Date(document.start).getTime() >=
            new Date(weekDatesArray[columnIndex]).getTime() &&
            new Date(document.end).getTime() <=
              new Date(weekDatesArray[columnIndex]).getTime()) ||
            (new Date(weekDatesArray[columnIndex]).getTime() >=
              new Date(document.start).getTime() &&
              new Date(weekDatesArray[columnIndex]).getTime() <=
                new Date(document.end).getTime())) &&
          document.installer === ""
      )
      .filter(
        (document) =>
          document.encodedSmallTitle !== "" && document.encodedLargeTitle !== ""
      )
      .map((project) => (
        <JobTile
          key={project.id}
          project={project}
          screenName="optimizer"
          projectDetailClick={onProjectDetailClick}
          dragStarted={onDragStarted}
          className="bg-skin-primary"
          isParkedOriginal={isJobParked(project.id)}
          onParkJob={onParkJob}
          diff={showDiffs ? lookupDiffForProject(project) : null}
          diffMuted={isDiffHighlightsMuted}
        ></JobTile>
      ));
  };

  // - - - - - - - - - -

  /**
   * Renders the unassigned cell.
   * @returns {JSX.Element} - The rendered unassigned cell.
   */
  const renderUnassignedCell = () => {
    return (
      <button
        key={"Unassigned"}
        className="w-full text-dark-grey text-sm font-semibold flex-1 basis-32 "
        onClick={() => onUnassignedAddClick()}
      >
        Unassigned
      </button>
    );
  };

  // - - - - - - - - - -

  /**
   * Renders the crew cell.
   * @param {Object} document - The crew document.
   * @returns {JSX.Element} - The rendered crew cell.
   */
  // weekDatesArray taken as a parameter (not closed over) — #132 fix. Each
  // <OptimizerGrid> owns its own local weekDatesArray since #105 multi-grid;
  // the grid passes its own array to renderCrewCell, which forwards it to
  // onCrewDetailClick so the CrewDetailModal opens on the SAME week the
  // user is looking at.
  const renderCrewCell = (document, weekDatesArray) => {
    const furnitureFlag =
      document.capacity_carpet_once_furniture > 0 ||
      document.capacity_vinyl_once_furniture > 0 ||
      document.capacity_vinylSheet_once_furniture > 0 ||
      document.capacity_tile_once_furniture > 0 ||
      document.capacity_backsplash_once_furniture > 0 ||
      document.capacity_floating_once_furniture > 0 ||
      document.capacity_glueDown_once_furniture > 0 ||
      document.capacity_nailDown_once_furniture > 0
        ? true
        : false;

    const floatingFlag = document.capacity_floating_once > 0 ? true : false;
    const glueDownFlag = document.capacity_glueDown_once > 0 ? true : false;
    const nailDownFlag = document.capacity_nailDown_once > 0 ? true : false;
    const vinylFlag = document.capacity_vinylSheet_once > 0 ? true : false;
    const tileFlag = document.capacity_tile_once > 0 ? true : false;
    const backsplashFlag = document.capacity_backsplash_once > 0 ? true : false;
    const interiorDoorFlag =
      document.capacity_interior_doors > 0 ? true : false;
    const exteriorDoorFlag =
      document.capacity_exterior_doors > 0 ? true : false;
    const stormDoorFlag = document.capacity_storm_doors > 0 ? true : false;
    const windowFlag = document.capacity_window > 0 ? true : false;

    // add tooltips to emoji icons
    var tooltipText = "";
    tooltipText = floatingFlag ? `🛟 floating\n` : tooltipText;
    tooltipText = glueDownFlag ? tooltipText + `🖌️ gluedown\n` : tooltipText;
    tooltipText = nailDownFlag ? tooltipText + `🔨 nailDown\n` : tooltipText;
    tooltipText = vinylFlag ? tooltipText + `📄 vinyl\n` : tooltipText;
    tooltipText = tileFlag ? tooltipText + `🪨 tile\n` : tooltipText;
    tooltipText = backsplashFlag
      ? tooltipText + `🌊 backsplash\n`
      : tooltipText;
    tooltipText = interiorDoorFlag
      ? tooltipText + `🚪 int door\n`
      : tooltipText;
    tooltipText = exteriorDoorFlag
      ? tooltipText + `🏠 ext door\n`
      : tooltipText;
    tooltipText = stormDoorFlag ? tooltipText + `🌪️ stm door\n` : tooltipText;
    tooltipText = windowFlag ? tooltipText + `🪟 window\n` : tooltipText;

    const emptyString = "";
    const ltrLine = formatCrewLtrLine(crewLtrByPayroll.get(dataFormatting_payrollFields(document.payrollNo)));

    return (
      <span className="block h-full px-2 font-medium">
        <span className="block w-full h-full font-bold tooltip relative">
          <button
            key={document.__ID_Candidate}
            data-testid="crew-cell"
            className={
              // 2025-05-12 - compliance (JIRA SW-709): Scheduling_Hold_Flag adds a line-through to the span
              // 2025-01-28 - DISABLED: Replaced by Scheduling_Hold_Effective_Date; keeping for potential re-enable
              // document.Scheduling_Hold_Flag
              //   ? "flex items-center justify-center w-full text-black text-sm font-semibold basis-32 whitespace-normal line-through"
              //   : "flex items-center justify-center w-full text-black text-sm font-semibold basis-32 whitespace-normal"
              "flex items-center justify-center w-full text-black text-sm font-semibold basis-32 whitespace-normal"
            }
            onClick={() => onCrewDetailClick(document.__ID_Candidate, weekDatesArray)}
            onMouseEnter={
              tooltipText ? (e) => handleMouseEnter(e, tooltipText) : undefined
            }
            onMouseLeave={handleMouseLeave}
          >
            {/* One block for the whole label: the button is a flex row, and a separate element
                for the name line would make it its own flex item beside the payroll (#695). */}
            <span className="block">
              <span>
                {document.Name_Full}
                {/* #695 — ℹ️ moved up from its own line to the end of the name (makes room for
                    the LTR line). A span, not a nested <button> (invalid inside the crew button);
                    stopPropagation so it opens the crew-meta modal, not the crew detail. */}
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="crew-meta-button"
                  className="ml-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCrewMetaClick(document.__ID_Candidate);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onCrewMetaClick(document.__ID_Candidate);
                    }
                  }}
                >
                  ℹ️
                </span>
              </span>
              <br />
              {dataFormatting_payrollFields(document.payrollNo)}
              <br />
              {emptyString
                .concat("", furnitureFlag ? "🪑" : "")
                .concat("", floatingFlag ? "🛟" : "")
                .concat("", glueDownFlag ? "🖌️" : "")
                .concat("", nailDownFlag ? "🔨" : "")
                .concat("", vinylFlag ? "📄" : "")
                .concat("", tileFlag ? "🪨" : "")
                .concat("", backsplashFlag ? "🌊" : "")
                .concat("", interiorDoorFlag ? "🚪" : "")
                .concat("", exteriorDoorFlag ? "🏠" : "")
                .concat("", stormDoorFlag ? "🌪️" : "")
                .concat("", windowFlag ? "🪟" : "")}
            </span>
          </button>
          {ltrLine && (
            <span data-testid="crew-ltr" className="text-black text-xs font-medium flex-1 flex justify-center whitespace-nowrap">
              {ltrLine}
            </span>
          )}
        </span>
        {/* Tooltip rendered in portal */}
        {tooltip.show && tooltip.text && (
          <TooltipPortal position={tooltip.position}>
            {tooltip.text}
          </TooltipPortal>
        )}
      </span>
    );
  };

  // - - - - - - - - - -

  /**
   * Renders the assigned scheduled cells for a given column and crew.
   * @param {number} columnIndex - The column index.
   * @param {Object} document - The crew document.
   * @param {boolean} isLockedCell - Whether the cell is locked.
   * @returns {JSX.Element[]} - The rendered assigned scheduled cells.
   */
  // weekDatesArray + scheduleArray taken as parameters — phase 2 + 4b.
  const renderAssignedScheduledCells = (
    columnIndex,
    document,
    isLockedCell,
    weekDatesArray,
    scheduleArray,
    showDiffs = false
  ) => {
    // ------------- SORT BY ARRIVAL -------------
    const arrivalOrder = [
      "8-11",
      "8 AM - 11 AM",
      "11-2",
      "11 AM - 2 PM",
      "2-5",
      "2 PM - 5 PM",
    ];
    const getArrivalIndex = (arrival) => {
      const idx = arrivalOrder.indexOf(arrival);
      return idx !== -1 ? idx : 99; // unknown values go last
    };

    // SUP-9005 — build the Lock/Unlock menu item ONCE for this cell and hand it
    // to each JobTile, so right-clicking a scheduled job surfaces Park AND
    // Lock/Unlock in a single menu. Previously the cell's own right-click lock
    // menu (#174) shadowed JobTile's Park menu, making Park appear to vanish.
    // buildLockMenuItem is the same helper the cell menu uses → identical
    // label/disabled/tooltip; only the onClick (tsToggleCellLock) differs.
    const cellPayroll = dataFormatting_payrollFields(document.payrollNo);
    const cellDate = weekDatesArray[columnIndex];
    const { lockable } = getCellLockEligibility(
      scheduleArray,
      cellPayroll,
      cellDate
    );
    const lockMenuItem = {
      ...buildLockMenuItem({ locked: isLockedCell, lockable, isTimeTraveling }),
      onClick: () => tsToggleCellLock(cellDate, cellPayroll, !isLockedCell),
    };

    const cellProjects = scheduleArray
      .filter(
        (project) =>
          project.installer === dataFormatting_payrollFields(document.payrollNo)
      )
      .filter(
        (project) =>
          (new Date(project.start).getTime() >=
            new Date(weekDatesArray[columnIndex]).getTime() &&
            new Date(project.end).getTime() <=
              new Date(weekDatesArray[columnIndex]).getTime()) ||
          (new Date(weekDatesArray[columnIndex]).getTime() >=
            new Date(project.start).getTime() &&
            new Date(weekDatesArray[columnIndex]).getTime() <=
              new Date(project.end).getTime())
      )
      .filter(
        (project) =>
          project.encodedSmallTitle !== "" && project.encodedLargeTitle !== ""
      );

    // #360 (Mervin) — bucket the cell's entries into three groups so real jobs
    // read first, the "SHARED <loc>" entries collapse into one bar, and the
    // attribute rows (OFF / BUFFER / custom / compliance hold) stay last:
    //     jobs  →  ▸ Shared (N)  →  attributes
    // Only the shared rows change; job + attribute tiles keep their exact
    // props/behavior. cellEntryGroup() is the pure, tested classifier.
    const jobTiles = [];
    const sharedTiles = [];
    const attrTiles = [];

    cellProjects.forEach((project) => {
      const projectDiff = showDiffs ? lookupDiffForProject(project) : null;
      if (project.complianceHold) {
        // Compliance hold entries render as-is (not draggable, not clickable).
        attrTiles.push(
          <JobTile
            key={project.id}
            project={project}
            screenName="optimizer"
            draggable={false}
            lockMenuItem={lockMenuItem}
            diff={projectDiff}
            diffMuted={isDiffHighlightsMuted}
          ></JobTile>
        );
        return;
      }

      const group = cellEntryGroup(project, locationFilter);
      if (group === "shared") {
        // #360 — a job assigned to this installer at ANOTHER location. Collapsed
        // into SharedLocationsGroup, and NO LONGER CLICKABLE (projectDetailClick
        // removed): these are context, not this location's jobs.
        project.encodedSmallTitle = "SHARED " + project.location;
        project.encodedLargeTitle = "SHARED " + project.location;
        sharedTiles.push(
          <JobTile
            key={project.id}
            project={project}
            screenName="optimizer"
            draggable={false}
            lockMenuItem={lockMenuItem}
            diff={projectDiff}
            diffMuted={isDiffHighlightsMuted}
          ></JobTile>
        );
        return;
      }

      // group === "job" or "attribute": same tile the location===locationFilter
      // branch always rendered (JobTile branches internally on project.attribute,
      // so OFF/BUFFER/custom keep their existing look + behavior). Only the
      // GROUP it lands in changes.
      const tile = (
        <JobTile
          key={project.id}
          project={project}
          // #188 (Brandon 2026-06-04) — a job/repair on a LOCKED day must NOT be
          // draggable OUT of that day; unlock first. JobTile ignores drag/park
          // for attribute tiles, so passing these is a no-op for OFF/BUFFER.
          draggable={!isLockedCell && !isJobParked(project.id)}
          screenName="optimizer"
          projectDetailClick={onProjectDetailClick}
          dragStarted={onDragStarted}
          isParkedOriginal={isJobParked(project.id)}
          onParkJob={onParkJob}
          lockMenuItem={lockMenuItem}
          diff={projectDiff}
          diffMuted={isDiffHighlightsMuted}
        ></JobTile>
      );
      if (group === "attribute") attrTiles.push(tile);
      else jobTiles.push(tile);
    });

    // Jobs keep the arrival-time sort; attributes keep their arrival sort too
    // (OFF/BUFFER have no arrival → index 99, unchanged order). Shared tiles keep
    // their natural order inside the collapsed group.
    const byArrival = (a, b) =>
      getArrivalIndex(a.props.project.encodedArrival) -
      getArrivalIndex(b.props.project.encodedArrival);
    jobTiles.sort(byArrival);
    attrTiles.sort(byArrival);

    // FAV 2.0 ghost bookings (#642): where FAV 2.0 WOULD have put an open Inbox job —
    // this crew, this day. Advice only, after the real jobs; never while time-travelling.
    // The job card itself comes from the FULL list (the ghost's day may be outside the
    // week the real, unassigned booking sits in).
    const ghostTiles = isTimeTraveling
      ? []
      : (favGhostsByCell.get(ghostCellKey(cellPayroll, cellDate)) || []).map((g) => (
          <FavGhostTile
            key={`fav-ghost-${g.rowId}-${g.day}`}
            ghost={g}
            project={allProjectsForGhosts.find((p) => jobKey(p) === g.jobId) || null}
            onOpen={(p) => onProjectDetailClick(p)}
          />
        ));

    const theCell = [
      ...jobTiles,
      ...ghostTiles,
      sharedTiles.length ? (
        <SharedLocationsGroup key="shared-locations" tiles={sharedTiles} />
      ) : null,
      ...attrTiles,
    ].filter(Boolean);

    return theCell;
  };

  // - - - - - - - - - -

  /**
   * Renders the surface calculated value for a given column and crew.
   * @param {number} columnIndex - The column index.
   * @param {Object} crew - The crew document.
   * @returns {String} - The surface value.
   */
  const renderSelectedSurfaceValues = (columnIndex, crew) => {
    var theValue = "";
    if (selectedUnscheduledJob) {
      switch (selectedUnscheduledJob.surface) {
        case "Carpet":
          theValue = crew.capacity_carpet_once;
          break;
        case "Vinyl":
          theValue = crew.capacity_vinylSheet_once;
          break;
        case "Plank":
          theValue = crew.capacity_vinyl_once;
          break;
        case "Tile":
          theValue = crew.capacity_tile_once;
          break;
        case "Wood":
          theValue = crew.capacity_floating_once;
          break;
        case "Backsplash":
          theValue = crew.capacity_backsplash_once;
          break;
        case "Pad":
          theValue = "";
          break;
        case "InteriorDoor":
          theValue = crew.capacity_interior_doors;
          break;
        case "ExteriorDoor":
          theValue = crew.capacity_exterior_doors;
          break;
        case "StormDoor":
          theValue = crew.capacity_storm_doors;
          break;
        case "Window":
          theValue = crew.capacity_window;
          break;

        default:
          theValue = "";
          break;
      }
      //
    } else {
      // selectedUnscheduledJob = null
      theValue = "";
    }

    return theValue;
  };

  // - - - - - - - - - -

  useEffect(() => {
    console.log(
      "ScheduleScreen useEffect[selectedUnscheduledJob] selectedUnscheduledJob: ",
      selectedUnscheduledJob
    );
  }, [selectedUnscheduledJob]);

  // - - - - - - - - - -
  // Time Machine #114 Phase 6 — Diff Pair scroll sync. The LEFT grid's
  // scroll position drives the RIGHT grid via direct DOM mutation. The
  // right grid hides its own scrollbar and rides the left's. We use a
  // re-entry guard so a programmatic scrollTop set on the right doesn't
  // re-fire its own onScroll handler (we don't attach one, but the guard
  // is cheap insurance if we ever go bidirectional).
  const diffPairLeftScrollRef = useRef(null);
  const diffPairRightScrollRef = useRef(null);

  // - - - - - - - - - -
  // #135 — Diff Pair row-height coordinator. Each grid's Unassigned row +
  // crew rows wrap themselves in <DiffPairRowSync> which reports its own
  // offsetHeight here; both grids read back max(left, right) and apply it
  // as min-height so rows stay aligned row-by-row under shared scroll.
  //
  // Stored in a ref (not state) so React state churn doesn't cascade
  // through both grids on every observer fire. A useReducer-driven force-
  // update triggers a re-render only when a height actually changed.
  // The pure helper `recordRowHeight` deduplicates no-op writes (same
  // height on same side → returns the SAME reference); we skip the
  // force-update in that case.
  const diffPairRowHeightsRef = useRef({});
  const [, forceDiffPairRowHeightsRerender] = useReducer((x) => x + 1, 0);
  const reportDiffPairRowHeight = useCallback((rowId, side, height) => {
    const next = recordRowHeight(
      diffPairRowHeightsRef.current,
      rowId,
      side,
      height
    );
    if (next !== diffPairRowHeightsRef.current) {
      diffPairRowHeightsRef.current = next;
      forceDiffPairRowHeightsRerender();
    }
  }, []);
  const getDiffPairRowMinHeight = useCallback(
    (rowId) => computeSyncedRowMinHeight(diffPairRowHeightsRef.current, rowId),
    []
  );
  const diffPairRowSyncLeft = useMemo(
    () => ({
      side: "left",
      reportRowHeight: reportDiffPairRowHeight,
      getRowMinHeight: getDiffPairRowMinHeight,
    }),
    [reportDiffPairRowHeight, getDiffPairRowMinHeight]
  );
  const diffPairRowSyncRight = useMemo(
    () => ({
      side: "right",
      reportRowHeight: reportDiffPairRowHeight,
      getRowMinHeight: getDiffPairRowMinHeight,
    }),
    [reportDiffPairRowHeight, getDiffPairRowMinHeight]
  );
  // Reset all measurements when the Diff Pair view toggles off OR the
  // visible week changes — the row tree remounts, accumulated min-heights
  // would be stale.
  //
  // Extracted into a primitive dependency so react-hooks/exhaustive-deps
  // can statically check it (the rule rejects complex expressions inline
  // in the deps array).
  const diffPairResetKey =
    grids[0]?.weekStartDate instanceof Date
      ? grids[0].weekStartDate.getTime()
      : null;
  useEffect(() => {
    diffPairRowHeightsRef.current = {};
  }, [isDiffPairView, diffPairResetKey]);
  const onDiffPairLeftScroll = (e) => {
    const right = diffPairRightScrollRef.current;
    if (!right) return;
    const left = e.target;
    if (right.scrollTop !== left.scrollTop) right.scrollTop = left.scrollTop;
    if (right.scrollLeft !== left.scrollLeft) right.scrollLeft = left.scrollLeft;
  };
  // Two-finger trackpad scroll on the right grid would scroll its own
  // overflow-auto container regardless of the hidden scrollbar. Forward
  // wheel deltas to the LEFT grid's scroller so the LEFT remains the
  // single source of scroll truth (and its onScroll handler then mirrors
  // the position back to the right). preventDefault stops the right's
  // own scroll position from drifting.
  const onDiffPairRightWheel = (e) => {
    const left = diffPairLeftScrollRef.current;
    if (!left) return;
    e.preventDefault();
    left.scrollBy({ left: e.deltaX, top: e.deltaY, behavior: "auto" });
  };

  // - - - - - - - - - -

  // schedule-page CSS sets overflow:auto for the legacy Scheduler tab.
  // Override inline so the Optimizer doesn't scroll horizontally as a
  // whole — only the inner grids row does. This keeps GlobalToolbar
  // centered on the window width and stops the RTS sidebar from scrolling.
  return (
    <div className="schedule-page bg-skin-base" style={{ overflow: "hidden" }}>
      <CrewScoresFetcher companyCode={companyCode} payrollNos={crewPayrolls} onScores={setCrewLtrByPayroll} />
      <div className="shrink-0" style={{ width: rtsSidebarWidth }}>
        <UnassignedProjects />
      </div>
      <Splitter
        onMouseDown={startSidebarResize}
        isDragging={isResizingSidebar}
      />
      <div className="flex-auto flex flex-col min-w-0">
        <div className="px-4 py-1">
          <GlobalToolbar
            onAddGrid={
              // Time Machine #114 Phase 4.5 hid this entirely in time-travel.
              // Phase 6 brings it back as a SINGLE-GRID ↔ DIFF-PAIR toggle
              // (not free-form multi-grid). One click splits the schedule
              // into LEFT (T1) + RIGHT (T2 = next event after T1) with diff
              // highlights; another click collapses back. Live mode keeps
              // its add-a-grid behavior unchanged.
              isTimeTraveling
                ? () => setIsDiffPairView((prev) => !prev)
                : () => addGridAfter(grids[grids.length - 1].id)
            }
            addGridTitle={
              isTimeTraveling
                ? isDiffPairView
                  ? "Collapse Diff Pair view (return to single grid)"
                  : "Open Diff Pair view (split-screen with red diff highlights)"
                : "Add a new schedule grid to the right"
            }
            addGridAriaLabel={
              isTimeTraveling
                ? "Toggle Diff Pair view"
                : "Add schedule grid"
            }
            addGridActive={isTimeTraveling && isDiffPairView}
            addGridIsToggle={isTimeTraveling}
          />
        </div>
        {/* Horizontal divider matches the vertical Splitter's idle color
            (#104) — separates the global toolbar from the schedule grids
            below. Static (no hover, no drag) per #105 phase 3 spec.
            Negative left margin extends it over the vertical splitter to
            create a T-junction (splitter is w-2 = 8px). */}
        <div
          className="h-0.5 shrink-0"
          style={{
            backgroundColor: "rgba(89, 89, 89, 0.4)",
            marginLeft: "-8px",
          }}
        />
        {/* Time Machine #114 Phase 6 — Diff Pair render fork. When the
            user toggles Diff Pair view from the time-travel banner, the
            multi-grid map is bypassed in favor of a fixed split-screen:
            LEFT grid uses the existing ScheduleContext (T1 data) and
            RIGHT grid is wrapped in a second <ScheduleContextProvider
            useRightSide> so it sees T2 data. Both grids share grids[0]'s
            week — week-nav on EITHER PerGridToolbar updates that single
            grid record and both grids re-render. Close (X) on either
            collapses Diff Pair view. RIGHT grid omits the Crews column
            (LEFT's serves both) and forwards wheel events to LEFT so
            sync-scroll never breaks. */}
        {isTimeTraveling && isDiffPairView ? (
          <div data-tutorial-target="diff-pair-view" className="flex-auto flex overflow-hidden min-w-0">
            {/* LEFT grid (T1) */}
            <div data-grid-id="diffpair-left" className="flex flex-1 min-w-0">
              <OptimizerGrid
                weekStartDate={grids[0].weekStartDate}
                setWeekStartDate={(d) => setGridWeek(grids[0].id, d)}
                onClose={() => setIsDiffPairView(false)}
                canClose={true}
                /* singleGrid=false (multi-grid layout) + cellWidthRem locks
                   each column to a fixed normal width (~10rem) regardless
                   of data — the row overflows the half-viewport container,
                   user scrolls horizontally to see the full week. Matches
                   single-grid Optimizer's column feel. */
                singleGrid={false}
                cellWidthRem={10}
                filteredCandidatesList={filteredCandidatesList}
                schedulePreparing={schedulePreparing}
                renderDateHeaderRow={renderDateHeaderRow}
                renderUnassignedCell={renderUnassignedCell}
                renderUnassignedScheduledCells={renderUnassignedScheduledCells}
                renderCrewCell={renderCrewCell}
                renderAssignedScheduledCells={renderAssignedScheduledCells}
                renderSelectedSurfaceValues={renderSelectedSurfaceValues}
                isLocked={isLocked}
                onCellDrop={onCellDrop}
                onCellDragOver={onCellDragOver}
                onCellDragLeave={onCellDragLeave}
                scrollContainerRef={diffPairLeftScrollRef}
                onScroll={onDiffPairLeftScroll}
                diffPairSide="left"
                diffPairRowSync={diffPairRowSyncLeft}
              />
            </div>
            <Splitter center />
            {/* RIGHT grid (T2) — second ScheduleContext provider with
                useRightSide=true. All time-travel mutation guards are
                already in place (Phase 4 three-layer defense). */}
            <ScheduleContextProvider useRightSide>
              <div data-grid-id="diffpair-right" className="flex flex-1 min-w-0">
                <OptimizerGrid
                  weekStartDate={grids[0].weekStartDate}
                  setWeekStartDate={(d) => setGridWeek(grids[0].id, d)}
                  onClose={() => setIsDiffPairView(false)}
                  canClose={true}
                  /* See LEFT grid above — same fixed cell width keeps both
                     grids aligned column-for-column. */
                  singleGrid={false}
                  cellWidthRem={10}
                  filteredCandidatesList={filteredCandidatesList}
                  schedulePreparing={schedulePreparing}
                  renderDateHeaderRow={renderDateHeaderRow}
                  renderUnassignedCell={renderUnassignedCell}
                  renderUnassignedScheduledCells={renderUnassignedScheduledCells}
                  renderCrewCell={renderCrewCell}
                  renderAssignedScheduledCells={renderAssignedScheduledCells}
                  renderSelectedSurfaceValues={renderSelectedSurfaceValues}
                  isLocked={isLocked}
                  onCellDrop={onCellDrop}
                  onCellDragOver={onCellDragOver}
                  onCellDragLeave={onCellDragLeave}
                  scrollContainerRef={diffPairRightScrollRef}
                  hideScrollbar
                  onWheel={onDiffPairRightWheel}
                  omitCrewsColumn
                  showDiffs
                  diffPairSide="right"
                  diffPairRowSync={diffPairRowSyncRight}
                />
              </div>
            </ScheduleContextProvider>
          </div>
        ) : (
        /* Only the grids row scrolls horizontally — GlobalToolbar and the
            RTS sidebar stay fixed when the user has more grids than the
            viewport can fit. */
        <div className="flex-auto flex overflow-x-auto min-w-0">
          {grids.map((grid, index) => {
            const isLast = index === grids.length - 1;
            // Rightmost grid is always flex-1 (takes remaining space) so
            // there's never empty area at the right edge. Other grids use
            // their persisted width when set, else flex-1 for equal share.
            // minWidth: MIN_GRID_WIDTH on every grid prevents flex from
            // crushing them when the viewport is too narrow — instead the
            // grids row overflows and horizontal scroll kicks in
            // (#105 phase 5 follow-up).
            const wrapperStyle = isLast
              ? { flex: "1 1 0", minWidth: MIN_GRID_WIDTH }
              : grid.width
              ? {
                  flex: "0 0 auto",
                  width: grid.width,
                  minWidth: MIN_GRID_WIDTH,
                }
              : { flex: "1 1 0", minWidth: MIN_GRID_WIDTH };
            return (
              <Fragment key={grid.id}>
                <div
                  ref={(el) => {
                    if (el) {
                      gridWrapperRefs.current[grid.id] = el;
                    } else {
                      delete gridWrapperRefs.current[grid.id];
                    }
                  }}
                  data-grid-id={grid.id}
                  className="flex"
                  style={wrapperStyle}
                >
                  <OptimizerGrid
                    weekStartDate={grid.weekStartDate}
                    setWeekStartDate={(d) => setGridWeek(grid.id, d)}
                    onClose={() => closeGrid(grid.id)}
                    canClose={grids.length > 1}
                    singleGrid={grids.length === 1}
                    filteredCandidatesList={filteredCandidatesList}
                    schedulePreparing={schedulePreparing}
                    renderDateHeaderRow={renderDateHeaderRow}
                    renderUnassignedCell={renderUnassignedCell}
                    renderUnassignedScheduledCells={
                      renderUnassignedScheduledCells
                    }
                    renderCrewCell={renderCrewCell}
                    renderAssignedScheduledCells={renderAssignedScheduledCells}
                    renderSelectedSurfaceValues={renderSelectedSurfaceValues}
                    isLocked={isLocked}
                    onCellDrop={onCellDrop}
                    onCellDragOver={onCellDragOver}
                    onCellDragLeave={onCellDragLeave}
                  />
                </div>
                {!isLast && (
                  <Splitter
                    onMouseDown={startSplitterDrag(grid.id)}
                    isDragging={draggingSplitterGridId === grid.id}
                    center
                  />
                )}
              </Fragment>
            );
          })}
        </div>
        )}
      </div>
      <ContextMenu
        position={
          contextMenu ? { x: contextMenu.x, y: contextMenu.y } : null
        }
        items={[
          // Time Machine #114 Phase 4.5 — Add Grid hidden during time-travel
          // (forensic single-view; multi-grid is for live planning).
          ...(isTimeTraveling
            ? []
            : [
                {
                  label: "Add grid to right",
                  onClick: () => addGridAfter(contextMenu?.gridId),
                },
              ]),
          {
            label: "Close this grid",
            disabled: grids.length <= 1,
            title:
              grids.length <= 1
                ? "At least one grid must remain open"
                : undefined,
            onClick: () => closeGrid(contextMenu?.gridId),
          },
        ]}
        onClose={() => setContextMenu(null)}
      />
    </div>
  );
};
